import { useView, PLATFORM, computedFrom } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { BenefitsPaymentsCentreApi } from '~/api/benefits-payments-centre-api';
import {
	DynamicFieldMeta,
	DynamicFieldTable,
} from '~/components/dynamic-field-table/dynamic-field-table';
import {
	FormModelListFieldTable,
	MoveRecordDirection,
} from '~/components/dynamic-field-table/form-model-list-field-table';
import { SearchFieldModel } from '~/components/field/common/search-definition';
import { FormType } from '~/components/form/form';
import { DataTypeEnum } from '~/models/data-type';
import { FieldModel, FormModel, Modifiable } from '~/models/form-model';
import { MemberPaymentHistoryField } from '~/models/generated/entities/MemberPaymentHistory';
import FormModelUtils from '~/utils/form-model-utils';
import { BenefitsPaymentsCentre } from '../benefits-payments-centre';
import {
	PaymentOptionalItemsDialogModel,
	PaymentOptionalItemsDialog,
} from '../payment-optional-items-dialog';
import {
	MessageValue,
	PaymentItemField,
	AbstractPaymentItemsList,
} from './abstract-payment-items-list';

@useView(PLATFORM.moduleName('./payment-items-list.html'))
export class DispersalsList extends AbstractPaymentItemsList {
	private totalMethod: ValueMethod;
	private preservedMethod: ValueMethod;

	constructor(...rest) {
		super(...rest);
		this.forceSaveFields = [
			...this.forceSaveFields,
			MemberPaymentHistoryField.PYz_Cheque_No,
			PaymentItemField.TOTAL_TYPE,
			PaymentItemField.PRESERVED_TYPE,
		];
		this.uniqueFieldMeta = PaymentItemField.DISPERSAL_LINE;
		this.headerClassName = 'dispersals';
		this.headerText = 'benefits-payments-centre:dispersals';
		this.addRecordButtonTitle = 'benefits-payments-centre:add-dispersal';
	}

	// Count of items in the form model that are dispersals
	@computedFrom('formModel')
	private get tableRowsCount() {
		return this.formModel.formModelList.filter(
			(formModelItem, rowIndex) => {
				const suffix = DynamicFieldTable.getSuffix(rowIndex + 1);
				return this.isPaymentItemPartOfList(formModelItem, suffix);
			},
		).length;
	}

	@computedFrom('formModel')
	protected get isListEditable(): boolean {
		return !BenefitsPaymentsCentre.hasBatchProcessCommenced(this.formModel);
	}

	@computedFrom('formModel')
	protected get renderAddRecord(): boolean {
		return !BenefitsPaymentsCentre.hasBatchProcessCommenced(this.formModel);
	}

	@computedFrom('formModel')
	protected get renderToggleTotalMethod(): boolean {
		return !BenefitsPaymentsCentre.hasBatchProcessCommenced(this.formModel);
	}

	@computedFrom('formModel')
	protected get renderTogglePreservedMethod(): boolean {
		return !BenefitsPaymentsCentre.hasBatchProcessCommenced(this.formModel);
	}

	protected formModelChanged(formModel: FormModel, oldFormModel: FormModel) {
		super.formModelChanged(formModel, oldFormModel);
		if (!formModel) {
			return;
		}
		const formModelList = formModel.formModelList;
		if (formModelList) {
			if (
				this.tableRowsCount < 1 &&
				(!oldFormModel ||
					formModel.fields.BSl_Batch_Reference.value !==
						oldFormModel.fields.BSl_Batch_Reference.value)
			) {
				this.totalMethod = ValueMethod.PERCENTAGE;
				this.preservedMethod = ValueMethod.PERCENTAGE;
			} else if (this.tableRowsCount > 0) {
				this.totalMethod = formModelList[this.tableRowsCount - 1]
					.fields[PaymentItemField.TOTAL_TYPE].value as ValueMethod;
				this.preservedMethod = formModelList[this.tableRowsCount - 1]
					.fields[PaymentItemField.PRESERVED_TYPE]
					.value as ValueMethod;
			}
		}
	}

	protected formModelChangedListItemProcessing(
		childFormModel: FormModel,
		parentFormModel: FormModel,
		rowIndex,
		suffix,
	) {
		super.formModelChangedListItemProcessing(
			childFormModel,
			parentFormModel,
			rowIndex,
			suffix,
		);
		// Conversation ID setup
		const conversationIdFieldModel: SearchFieldModel =
			childFormModel.fields[PaymentItemField.CONV_ID + suffix];
		if (conversationIdFieldModel) {
			DispersalsList.setupConvIdSearchField(conversationIdFieldModel);
			DispersalsList.setupConvIdAdditionalFilters(
				conversationIdFieldModel,
				rowIndex,
				this.selectedConversationIds,
				parentFormModel,
			);
		}
		// Set this rowIndex's selectedConversationIds
		const recordNumber = childFormModel.fields[PaymentItemField.MS_REC_NO];
		if (recordNumber) {
			this.selectedConversationIds[rowIndex] = recordNumber.value;
		}
		// Add subscription to message field
		this.addMessageSubscription(childFormModel, rowIndex, rowIndex, suffix);
	}

	// Used in view
	private onAddRecord = (): void => {
		DispersalsList.addDispersalAndGetFormModel(
			this.formModel,
			this.listFieldMeta,
			this.totalMethod,
			this.preservedMethod,
			this.i18n,
			this.tableRowsCount,
		).then((model) => (this.formModel = model));
	};

	// Used in view
	private onMoveRecord = (): void => {
		this.formModel = DispersalsList.swapMethodsAndGetFormModel(
			this.formModel,
			this.i18n,
			this.tableRowsCount,
		);
	};

	// Used in view
	private onToggleTotalMethod = (): void => {
		this.formModel = DispersalsList.toggleTotalMethodAndGetFormModel(
			this.formModel,
			this.totalMethod,
			this.i18n,
			this.tableRowsCount,
		);
		this.totalMethod =
			this.totalMethod === ValueMethod.PERCENTAGE
				? ValueMethod.REMAINDER
				: ValueMethod.PERCENTAGE;
	};

	// Used in view
	private onTogglePreservedMethod = (): void => {
		this.formModel = DispersalsList.togglePreservedMethodAndGetFormModel(
			this.formModel,
			this.preservedMethod,
			this.i18n,
			this.tableRowsCount,
		);
		this.preservedMethod =
			this.preservedMethod === ValueMethod.PERCENTAGE
				? ValueMethod.REMAINDER
				: ValueMethod.PERCENTAGE;
	};

	// Used in view
	private onRemoveRecord = (rowIndex: number, totalRows: number): void => {
		this.formModel = DispersalsList.removeDispersalAndGetFormModel(
			this.formModel,
			rowIndex,
			totalRows,
			this.listFieldMeta,
			this.i18n,
		);
		this.selectedConversationIds[rowIndex] = '';
		this.updateSelectedConvIdFilterOfFormModel();
	};

	protected onOptionalItemsButtonClick = (
		rowNum: number,
		totalRows: number,
	): void => {
		const formModel = this.formModel.formModelList[rowNum];
		const paymentOptionalItemsDialogModel: PaymentOptionalItemsDialogModel =
			{
				functionName: this.functionName,
				formType: FormType.EDIT,
				customisationConfig: this.customisationConfig,
				areaName: 'paymentOptionalItems',
				formModel,
				isReadOnly: BenefitsPaymentsCentre.hasBatchProcessCommenced(
					this.formModel,
				),
			};
		this.classicPanelDialogUtils.showCustomClassicPanelDialog(
			PaymentOptionalItemsDialog,
			paymentOptionalItemsDialogModel,
		);
	};

	protected onSelectConvID = (
		rowIndex: number,
		record: { [columnName: string]: any },
		value: string,
		totalRows: number,
	): void => {
		this.updateConvIDField(rowIndex, rowIndex, record);
	};

	protected onSelectPayee = (
		rowIndex: number,
		record: { [columnName: string]: any },
		value: string,
		totalRows: number,
	): void => {
		this.formModel = DispersalsList.selectPayeeAndGetFormModel(
			this.formModel,
			rowIndex,
			record,
			value,
		);
	};

	private static swapMethodsAndGetFormModel(
		formModel: FormModel,
		i18n: I18N,
		tableRowsCount: number,
	): FormModel {
		const formModelList = formModel.formModelList;
		const lastFormModel = formModelList[tableRowsCount - 1];
		const secondLastFormModel = formModelList[tableRowsCount - 2];

		// if moving a payment record has caused a dollar payment to be last and
		// a remainder payment to be second last, swap their payment methods
		if (
			(lastFormModel.fields[PaymentItemField.TOTAL_TYPE].value ===
				ValueMethod.DOLLAR &&
				secondLastFormModel.fields[PaymentItemField.TOTAL_TYPE]
					.value === ValueMethod.REMAINDER) ||
			(lastFormModel.fields[PaymentItemField.PRESERVED_TYPE].value ===
				ValueMethod.DOLLAR &&
				secondLastFormModel.fields[PaymentItemField.PRESERVED_TYPE]
					.value === ValueMethod.REMAINDER)
		) {
			DispersalsList.valueDollarToRemainder(
				lastFormModel.fields,
				tableRowsCount,
				i18n,
			);
			DispersalsList.valueRemainderToDollar(
				secondLastFormModel.fields,
				tableRowsCount - 1,
				i18n,
			);
		}

		const updatedFormModel = FormModelUtils.cloneModel(formModel);
		updatedFormModel.formModelList = formModelList;
		return updatedFormModel;
	}

	/**
	 * Change Total/Preserved Method from Dollar to Remainder, make field readonly and clear value
	 * Used when removing dispersal record so last dispersal has a Remainder method or when swapping
	 * Dollar and Remainder records
	 */
	private static valueDollarToRemainder(
		dispersalFields: { [key in string]: FieldModel },
		rowId: number,
		i18n: I18N,
	): void {
		if (
			dispersalFields[PaymentItemField.TOTAL_TYPE].value ===
			ValueMethod.DOLLAR
		) {
			dispersalFields[PaymentItemField.TOTAL_TYPE].value =
				ValueMethod.REMAINDER;
			const totalValueField =
				dispersalFields[
					PaymentItemField.TOTAL_VALUE +
						DynamicFieldTable.getSuffix(rowId)
				];
			totalValueField.value = undefined;
			totalValueField.modifiable = Modifiable.FALSE;
			totalValueField.required = false;
			totalValueField.label = i18n.tr(
				'benefits-payments-centre:dispersal-total-remainder',
			);
		}

		if (
			dispersalFields[PaymentItemField.PRESERVED_TYPE].value ===
			ValueMethod.DOLLAR
		) {
			dispersalFields[PaymentItemField.PRESERVED_TYPE].value =
				ValueMethod.REMAINDER;
			const preservedValueField =
				dispersalFields[
					PaymentItemField.PRESERVED_VALUE +
						DynamicFieldTable.getSuffix(rowId)
				];
			preservedValueField.value = undefined;
			preservedValueField.modifiable = Modifiable.FALSE;
			preservedValueField.required = false;
			preservedValueField.label = i18n.tr(
				'benefits-payments-centre:dispersal-preserved-remainder',
			);
		}
	}

	/**
	 * Change Total/Preserved Method from Remainder to Dollar, make field modifiable and set dataType to Currency
	 * Used when adding new dispersal record so second last Total value has a Dollar method or when swapping
	 * Dollar and Remainder records on total field
	 */
	private static valueRemainderToDollar(
		dispersalFields: { [key in string]: FieldModel },
		rowId: number,
		i18n: I18N,
	): void {
		if (
			dispersalFields[PaymentItemField.TOTAL_TYPE].value ===
			ValueMethod.REMAINDER
		) {
			dispersalFields[PaymentItemField.TOTAL_TYPE].value =
				ValueMethod.DOLLAR;
			const totalValueField =
				dispersalFields[
					PaymentItemField.TOTAL_VALUE +
						DynamicFieldTable.getSuffix(rowId)
				];
			totalValueField.modifiable = Modifiable.TRUE;
			totalValueField.dataType = {
				name: DataTypeEnum.Currency,
				scale: 2,
			};
			totalValueField.required = true;
			totalValueField.label = i18n.tr(
				'benefits-payments-centre:dispersal-total-amount',
			);
		}

		if (
			dispersalFields[PaymentItemField.PRESERVED_TYPE].value ===
			ValueMethod.REMAINDER
		) {
			dispersalFields[PaymentItemField.PRESERVED_TYPE].value =
				ValueMethod.DOLLAR;
			const presValueField =
				dispersalFields[
					PaymentItemField.PRESERVED_VALUE +
						DynamicFieldTable.getSuffix(rowId)
				];
			presValueField.modifiable = Modifiable.TRUE;
			presValueField.dataType = { name: DataTypeEnum.Currency, scale: 2 };
			presValueField.required = true;
			presValueField.label = i18n.tr(
				'benefits-payments-centre:dispersal-preserved-amount',
			);
		}
	}

	private static async addDispersalAndGetFormModel(
		formModel: FormModel,
		dispersalDynamicFieldMeta: DynamicFieldMeta[],
		totalMethod: ValueMethod,
		preservedMethod: ValueMethod,
		i18n: I18N,
		tableRowsCount,
	): Promise<FormModel> {
		// Check if we reached the maximum number of rows
		if (tableRowsCount >= DispersalsList.MAX_ROWS_SIZE)
			return Promise.resolve(formModel);

		const formModelList = formModel.formModelList;
		let updatedFormModel: FormModel;
		// if there are no dispersal child records, we need to fetch a backend formModel
		if (tableRowsCount < 1) {
			return new Promise<FormModel>((resolve, reject) => {
				BenefitsPaymentsCentreApi.getEmptyDispersal(
					formModel,
					totalMethod,
					preservedMethod,
				).then((dispersal) => {
					// insert dispersal form model
					formModelList.push(dispersal);

					updatedFormModel = FormModelUtils.cloneModel(formModel);
					updatedFormModel.formModelList = formModelList;
					return resolve(updatedFormModel);
				});
			});
		}
		// clone last payment form model
		const lastDispersalRowId = tableRowsCount;
		const lastDispersalFormModel = formModelList[lastDispersalRowId - 1];
		const formModelToInsert = FormModelUtils.cloneModel(
			lastDispersalFormModel,
		);
		// if last payment is a remainder, change this to Dollar and make modifiable
		this.valueRemainderToDollar(
			lastDispersalFormModel.fields,
			lastDispersalRowId,
			i18n,
		);
		// increment row id on this payment form model
		FormModelListFieldTable.updateRowId(
			formModelToInsert.fields,
			PaymentItemField.PRIORITY,
			dispersalDynamicFieldMeta,
			lastDispersalRowId,
			MoveRecordDirection.DOWN,
		);
		// clear values from this payment form model
		this.defaultValuesFromInsertedRow(
			lastDispersalRowId,
			formModelToInsert,
			dispersalDynamicFieldMeta,
		);
		// calculate remainders
		this.calculatePercentageRemainder(
			lastDispersalRowId,
			formModelList,
			formModelToInsert,
			PaymentItemField.TOTAL_TYPE,
			PaymentItemField.TOTAL_VALUE,
		);
		this.calculatePercentageRemainder(
			lastDispersalRowId,
			formModelList,
			formModelToInsert,
			PaymentItemField.PRESERVED_TYPE,
			PaymentItemField.PRESERVED_VALUE,
		);
		// insert dispersal form model after last entry
		formModelList.push(formModelToInsert);

		updatedFormModel = FormModelUtils.cloneModel(formModel);
		updatedFormModel.formModelList = formModelList;
		return Promise.resolve(updatedFormModel);
	}

	private static calculatePercentageRemainder(
		lastDispersalRowId: number,
		formModelList: FormModel[],
		formModelToInsert: FormModel,
		dispersalFieldType: PaymentItemField,
		dispersalFieldValue: PaymentItemField,
	): void {
		let remainder = 100;
		for (let i = lastDispersalRowId - 1; i >= 0; i--) {
			if (
				formModelList[i].fields[dispersalFieldType].value !==
				ValueMethod.PERCENTAGE
			) {
				break;
			}
			let percentageValue =
				formModelList[i].fields[
					dispersalFieldValue + DynamicFieldTable.getSuffix(i + 1)
				].value;
			if (!percentageValue) {
				percentageValue = '0';
			}
			remainder -= parseFloat(percentageValue);
		}

		if (
			formModelToInsert.fields[dispersalFieldType].value ===
				ValueMethod.PERCENTAGE &&
			remainder > 0
		) {
			const suffix = DynamicFieldTable.getSuffix(lastDispersalRowId + 1);
			formModelToInsert.fields[dispersalFieldValue + suffix].value =
				String(remainder);
		}
	}

	private static toggleTotalMethodAndGetFormModel(
		benefitsPaymentsFormModel: FormModel,
		totalMethod: ValueMethod,
		i18n: I18N,
		tableRowsCount,
	): FormModel {
		const formModelList = benefitsPaymentsFormModel.formModelList;
		let rowId = 1;

		if (totalMethod === ValueMethod.PERCENTAGE) {
			formModelList.forEach((formModel) => {
				// change payment method to Dollar and Payment Value dataType to Currency
				formModel.fields[PaymentItemField.TOTAL_TYPE].value =
					ValueMethod.DOLLAR;
				const totalValueField =
					formModel.fields[
						PaymentItemField.TOTAL_VALUE +
							DynamicFieldTable.getSuffix(rowId)
					];
				totalValueField.modifiable = Modifiable.TRUE;
				totalValueField.dataType = {
					name: DataTypeEnum.Currency,
					scale: 2,
				};
				totalValueField.label = i18n.tr(
					'benefits-payments-centre:dispersal-total-amount',
				);
				rowId++;
			});
			// change last payment to remainder and make payment value read only
			if (tableRowsCount > 0) {
				this.valueDollarToRemainder(
					formModelList[tableRowsCount - 1].fields,
					tableRowsCount,
					i18n,
				);
			}
		} else {
			formModelList.forEach((formModel) => {
				// change payment method and Payment Value dataType to Percentage
				formModel.fields[PaymentItemField.TOTAL_TYPE].value =
					ValueMethod.PERCENTAGE;
				const totalValueField =
					formModel.fields[
						PaymentItemField.TOTAL_VALUE +
							DynamicFieldTable.getSuffix(rowId)
					];
				totalValueField.modifiable = Modifiable.TRUE;
				totalValueField.dataType = { name: DataTypeEnum.Percentage };
				totalValueField.required = true;
				totalValueField.label = i18n.tr(
					'benefits-payments-centre:dispersal-total-percentage',
				);
				rowId++;
			});
		}

		const model = FormModelUtils.cloneModel(benefitsPaymentsFormModel);
		model.formModelList = formModelList;
		return model;
	}

	private static togglePreservedMethodAndGetFormModel(
		benefitsPaymentsFormModel: FormModel,
		preservedMethod: ValueMethod,
		i18n: I18N,
		tableRowsCount: number,
	): FormModel {
		const formModelList = benefitsPaymentsFormModel.formModelList;
		let rowId = 1;

		if (preservedMethod === ValueMethod.PERCENTAGE) {
			formModelList.forEach((formModel) => {
				// change payment method to Dollar and Payment Value dataType to Currency
				formModel.fields[PaymentItemField.PRESERVED_TYPE].value =
					ValueMethod.DOLLAR;
				const preservedValueField =
					formModel.fields[
						PaymentItemField.PRESERVED_VALUE +
							DynamicFieldTable.getSuffix(rowId)
					];
				preservedValueField.modifiable = Modifiable.TRUE;
				preservedValueField.dataType = {
					name: DataTypeEnum.Currency,
					scale: 2,
				};
				preservedValueField.label = i18n.tr(
					'benefits-payments-centre:dispersal-preserved-amount',
				);
				rowId++;
			});
			// change last payment to remainder and make payment value read only
			if (tableRowsCount > 0) {
				this.valueDollarToRemainder(
					formModelList[tableRowsCount - 1].fields,
					tableRowsCount,
					i18n,
				);
			}
		} else {
			formModelList.forEach((formModel) => {
				// change payment method and Payment Value dataType to Percentage
				formModel.fields[PaymentItemField.PRESERVED_TYPE].value =
					ValueMethod.PERCENTAGE;
				const preservedValueField =
					formModel.fields[
						PaymentItemField.PRESERVED_VALUE +
							DynamicFieldTable.getSuffix(rowId)
					];
				preservedValueField.modifiable = Modifiable.TRUE;
				preservedValueField.dataType = {
					name: DataTypeEnum.Percentage,
				};
				preservedValueField.required = true;
				preservedValueField.label = i18n.tr(
					'benefits-payments-centre:dispersal-preserved-percentage',
				);
				rowId++;
			});
		}

		const model = FormModelUtils.cloneModel(benefitsPaymentsFormModel);
		model.formModelList = formModelList;
		return model;
	}

	private static removeDispersalAndGetFormModel(
		formModel: FormModel,
		rowIndex: number,
		totalRows: number,
		dispersalDynamicFieldMeta: DynamicFieldMeta[],
		i18n: I18N,
	): FormModel {
		const formModelList = formModel.formModelList;
		// if removing remainder payment, change last payment to Remainder,
		// clear value and make readonly
		if (
			(formModelList[rowIndex].fields[PaymentItemField.TOTAL_TYPE]
				.value === ValueMethod.REMAINDER ||
				formModelList[rowIndex].fields[PaymentItemField.PRESERVED_TYPE]
					.value === ValueMethod.REMAINDER) &&
			totalRows > 1
		) {
			this.valueDollarToRemainder(
				formModelList[rowIndex - 1].fields,
				rowIndex,
				i18n,
			);
		}
		const updatedFormModel = FormModelUtils.cloneModel(formModel);
		updatedFormModel.formModelList =
			DispersalsList.removeRowAndGetFormModelList(
				rowIndex,
				totalRows,
				formModelList,
				dispersalDynamicFieldMeta,
			);
		return updatedFormModel;
	}
}

enum ValueMethod {
	DOLLAR = 'D',
	PERCENTAGE = 'P',
	REMAINDER = 'R',
}
